import React from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { SimpleDialogContainer } from "react-simple-dialogs";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { lazyWithReload } from "@lib/chunkReload";
import { LocaleProvider } from "@lib/contexts/LocaleContext";
import { isPublicPath } from "@lib/publicPaths";

import { initializeSentry } from "./utils/sentry";
import { isContentfulRoute, routeChunks } from "./routeChunks";
import { AppRoutes } from "./routes";

import { Fallback } from "@/components/fallback";
import { QueryClientProvider } from "@/utils/QueryClientProvider";

// Initialize Sentry
initializeSentry();

// Lazy-loaded so the `contentful` client chunk (~79 KiB gzip) drops
// off the entry's modulepreload list entirely and isn't downloaded on
// /checkout. The smaller `@contentful` rich-text + live-preview chunk
// (17 KiB gzip) stays linked to the entry because multiple lazy
// member-area chunks reach it (ArticlePage, DateNightPage and their
// child components all use rich-text-react-renderer), and Rollup
// hoists shared deps of async children into the entry. The provider
// itself is still skipped on /checkout — that's where the runtime
// win is: no Contentful API query on mount, no React context tree
// for `useContentful()` consumers (which don't exist on /checkout).
// lazyWithReload, not lazy: a stale tab after a deploy 404s this chunk and
// would otherwise hang the whole member area on the Suspense fallback
// (Sentry NMH-REACT-2B3 lists ContentfulProviders-*.js; OMG-2594).
const ContentfulProviders = lazyWithReload(routeChunks.ContentfulProviders);

// Mount the Contentful provider stack on member routes only. Every
// useContentful() consumer is member-area content code (NewMemberHome,
// Collection, Article, DateNight and their selectors); nothing under
// /checkout, /gifts, /redeem or the auth routes reads it. Before OMG-1836
// only /checkout was skipped, so /login, /gifts and /redeem each paid the
// ContentfulProviders chunk (plus the 79 KB gzip `contentful` client) AND the
// member-home entry query (105 KB gzip) on every load, on the critical path:
// this wrapper sits around AppRoutes, so the route tree could not render
// until the lazy chunk resolved.
//
// The one thing /login got out of that was a warm cache for the /home it
// navigates to after sign-in. routeChunks.prefetchForPath keeps that: on auth
// routes it prefetches the chunk and the entry once the page is idle, off the
// critical path.
//
// /post-checkout stays a Contentful route on purpose: its provider query is
// what fills `useContentful().data` for the /home it hands off to, and
// NewMemberHome renders an empty fragment until that data arrives. Skipping
// it here once produced a ~3s white screen on mobile after "Start Exploring".
//
// Re-evaluates on SPA navigation, so a user crossing from /login or /checkout
// into a member route gets the providers mounted on demand. The path from
// useLocation is basename-relative; isContentfulRoute accepts both forms.
const MaybeContentfulProviders = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { pathname } = useLocation();
  if (!isContentfulRoute(pathname)) return <>{children}</>;
  return <ContentfulProviders>{children}</ContentfulProviders>;
};

function getBasename(): string {
  // Public paths render under "/", member pages under "/members". Shares
  // PUBLIC_PATHS with the Segment-source selection (see publicPaths.ts).
  return isPublicPath(window.location.pathname) ? "/" : "/members";
}

function App() {
  return (
    <React.Suspense fallback={<Fallback />}>
      <QueryClientProvider>
        {import.meta.env.DEV && <ReactQueryDevtools />}
        <BrowserRouter
          future={{
            v7_relativeSplatPath: false,
            v7_startTransition: false,
          }}
          basename={getBasename()}
        >
          <LocaleProvider>
            <SimpleDialogContainer />
            <MaybeContentfulProviders>
              <AppRoutes />
            </MaybeContentfulProviders>
          </LocaleProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.Suspense>
  );
}

export default App;
